/**
  * @class mmodUnits
  * @description
  * Units module displays other online operational units of the system.
  * It includes only those units that the user has permissions to see
  * - i.e. user must have assigned role in the same zone
  * as the unit’s user and the role can not be PASSIVE.
  * Unit is technically equal to online user.
  * For operational purpose field officers sets also a callsign during login process.
  * Callsign is also used for communication using radio.
  * Type of unit (i.e. field officer or back office user) are distinguished by an icon.
  * User has a shortcut button to send a message to particular unit’s user.
  * Module is implemented without workflow, as a pure module named mmodUnits.
  * It listens to mmApplications, mmAppGps messages which are sent to #APPS queue by mmodUnits module
  * and mmUsers messages sent by msysUser service and visualises data of units.
  * Module is used in both, mOffice and mPatrol.
  */

import mmodModule from 'mmodModule'

export default class mmodUnits extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: { type: 'object', default: { type: 'full' }, description: 'Initial window setup; same structure as required by `mmWindowSet`' },
      streaming: { type: 'boolean', description: 'whether streaming is enabled' },
      streaming_port: { type: 'integer', description: 'server streaming port', default: 8008 },
      delay_watch: { type: 'integer', description: 'delay of sending mmWatch after user logged', default: 1000 },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.users = {} // store users
    this.groups = {} // store groups
    this.applications = {} // store applications
    this.app_gps = {} // store GPS of applications
    this.applications_version = 0

    this.loadForm(require('./mmodUnits.json'))
    this.elList = this.el.find('>mele-body >@list')
    this.elDetail = this.el.find('>mele-body >@detail')

    if(!this.config.streaming) {
      this.el.forAll('.mele-button@streaming', el=> { el.hidden=true })
    }

    this.elMapInput = this.elDetail.find('@location')
    if(MOSY_APP==='mOffice') {
      // special tweak - map use always "day" theme in mOffice
      this.elDetail.forAll('.mele-map', el=>{ el.base='day' })
    }
    this.elList.bind(this.elDetail)
    this.elList.eventValueChanged = (el)=>{
      // enable/disable Track on Map button depending on whether location is available
      if(el.key==='location') {
        this.elDetail.find('@track').disabled = el.value==='/'
      }
      this.refreshMap()
    }

    this.mmStart()
    this.subscribe('#MAP')
    this.subscribe('#APPS')
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server notifies watchers that some system users were updated
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - whether all fields or just subset is sent
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} params.version - version of data sent by server
  * @param {array} params.list - array of changed records
  */
  mmUsers(sender, params) {
    super.mmUsers(sender, params)
    this.refreshList()
  } // mmUsers

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    this.wf = true // to know we are working with new workflow msvr
    if(params.type==='applications') {
      // send mmApplications to all modules when app changed
      delete params.type
      params.list.forEach(item=>{
        item.enabled = item.state==='ONLINE' || item.state==='OFFLINE'
        item.online = item.state==='ONLINE'
        item.deleted_dt = item.state==='DELETED' ? item.updated_dt : null
        delete item.state
        delete item.roles
      })
      this.mmApplications(sender, _.omit(params, ['limited']))

      // send mmAppGps to all modules when app changed
      const gps_params = { list: [] }
      gps_params.list = params.list.map(item=>_.pick(item, ['id', 'gps', 'speed', 'heading'])).filter(i=>!!i.gps) // gps is required for mmAppGps, so we filter out the item if gps is missing
      this.publish('#MAP', 'mmAppGps', gps_params)
    }
  } // mmObjects

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server sent list of applications
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.list] - array of changed records
  */
  mmApplications(sender, params) {
    if(sender.scope!==':') return

    if(params.version >= this.applications_version) {
      this.applications_version = params.version

      // forward info to #APPS channel
      this.publish('#APPS', 'mmApplications', params)

      if(params.full) this.applications = {}
      params.list.forEach(change=>{ this.applications[change.id] = change })
      this.refreshList(params.list.map(app=>app.id))
    }
  } // mmApplications

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information from server on location change of other application
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {array} params.list - array of changed records
  */
  mmAppGps(sender, params) {
    params.list.forEach(rec=>{
      this.app_gps[rec.id] = _.omit(rec, 'id')
      this.app_gps[rec.id].dt = Date.now()
    })
    this.refreshList(params.list.map(app=>app.id))
  } // mmAppGps

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * refresh list of users
  */
  refreshList(changed_appids) {
    const units = {}
    const full = !changed_appids
    if(!Array.isArray(changed_appids)) changed_appids = Object.keys(this.applications)
    const deleted = []
    changed_appids.forEach(app_id=>{
      if(app_id===this.app_id) return
      const app = this.applications[app_id]
      if(!app) return deleted.push(app_id)
      const user = this.users[app.user_id]
      if(!user) return deleted.push(app_id)
      user.fullname = _.toString(user.firstname) + ' ' + _.toString(user.lastname)
      const callsign = _.toString(app.callsign)
      if(callsign==='') return deleted.push(app_id)

      const location = this.app_gps[app_id] || {}
      location.gps = _.toString(location.gps)

      const unit = units[app_id] = {
        id: app_id,
        status: app.online>0 ? _.toString(app.status) || 'bout' : 'bout',
        location: location.gps,
      }
      const domains = {
        app,
        user,
        location,
      }
      for(const domain in domains) {
        // put all info from app, user and location as flat info into unit.data hash
        for(const p in domains[domain]) { unit[domain+'_'+p] = domains[domain][p] }
      }
    })
    this.elList.update({
      full,
      list: _.values(units),
    })
    if(!full && deleted.length>0) {
      this.elList.deleteItems(deleted)
    }
    this.refreshMap(changed_appids)

    const current_app = this.applications[this.elDetail.datakey]
    const streaming_disabled = !(current_app && current_app.peering_id)
    this.el.forAll('.mele-button@streaming', el=>{
      if(el.disabled!==streaming_disabled) el.disabled=streaming_disabled
    })
  } // refreshList

  refreshMap(changed_appids) {
    const app_id = this.elList.selected[0]
    if(Array.isArray(changed_appids) && changed_appids.indexOf(app_id)<0) return
    let unit = this.elList.getItem(app_id)
    unit = (unit ? unit.data :null) || {}
    const status = unit.status || ''
    this.elMapInput.updateProperties({
      itemlabel: unit.app_callsign,
      itemcolor: ({ g: 'green', o: 'orange', r: 'red', b: 'black' })[status.substr(0, 1)] || 'black',
    }, true)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    // ask to watch application changes
    // if user_id changed, ask for full update
    if(this.user_id !== params.user_id) this.applications_version = 0
    super.mmUserLogged(sender, params)

    // new workflow-based server
    setTimeout(()=>{
      if(this.user_id) {
        if(MOSY_APP==='mOffice') {
          this.publish(':msvrApplications', 'mmWatch', {
            version: 0,
            fields: ['id', 'state', 'apptype', 'appversion', 'note', 'device_id', 'vehicle', 'location',
              'peering_id', 'status', 'user_id', 'callsign', 'gps', 'speed', 'heading', 'ip', 'created_dt', 'connected_dt',
            ],
          })
        }else {
          this.publish(':msvrApplications', 'mmWatch', {
            version: this.applications_version,
            fields: ['id', 'state', 'apptype', 'status', 'user_id', 'callsign', 'gps', 'speed', 'heading'],
          })
        }
      }else{
        this.publish(':msvrApplications', 'mmWatch')
      }
    }, this.config.delay_watch)
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  mmButtonClicked(sender, params) {
    if(params.button==='track') {
      this.publish('msysWM', 'mmWindowFocus', { id: 'mmodMap' })
      setTimeout(()=>{
        this.publish('mmodMap', 'mmMapSelect', { type: 'unit', id: this.elDetail.datakey, zoom: 16 })
      }, 500)
    }else if(params.button==='message') {
      const item = this.elList.getItem(this.elDetail.datakey)
      const user_id = item && item.data ? item.data.user_id : null
      if(user_id>0) {
        this.publish('msysWM', 'mmWindowFocus', { id: 'mmodChats' })
        this.publish('mmodChats', 'mmChatNew', { user_id })
        this.publish('msysWM', 'mmWindowFocus', { id: 'mmodChats' })
        this.publish('mmodChats', 'mmChatNew', { user_id })
      }
    }else if(params.button==='streaming') {
      this.startStreaming()
    }
  } // mmButtonClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by <mele-list> when selected item change
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of the list
  * @param {string} params.selected - key attribute of the selected items separated by ∫
  * @param {string} [params.old] - key attribute of previously selected item
  * @param {boolean} [params.interactive] - indicates if this was user activity
  */
  mmListSelected(sender, params) {
    if(params.holder==='list') {
      this.elList.selected = params.selected.split('∫')
      this.refreshList()
    }
  } // mmListSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * start video streaming using mmodPlayer module
  */
  startStreaming() {
    if(!this.streaming_url) return
    const app = this.applications[this.elDetail.datakey]
    if(!app) return
    const mbox_appid = app.peering_id
    if(!mbox_appid) return

    const callsign = _.toString(app.callsign) || this.elDetail.datakey
    const user = this.users[app.user_id]
    const username = user ? _.toString(user.firstname) + ' ' + _.toString(user.lastname) : ''

    this.publish('msysWM', 'mmWindowFocus', { id: 'mmodPlayer' })
    this.publish('mmodPlayer', 'mmPlay', {
      url: this.streaming_url+'/'+mbox_appid,
      header: callsign,
      header2: username,
      icon: app.apptype ?'icon:app.apptype.'+app.apptype : '',
    })
  } // startStreaming

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    this.streaming_url = params.url.replace(/\/$/, '').replace(/(^wss?:\/\/[^:/]+)(?::(\d+))?/, '$1'+':'+this.config.streaming_port)
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Listeners informed others it joins the subscription queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.queue] - subscription queue, starting with #
  */
  mmSubscribed(sender, params) {
    if(params.queue==='#APPS') {
      this.publish(sender, 'mmApplications', { full: true, version: this.applications_version, list: Object.values(this.applications) })
    }
  } // mmSubscribed

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Ask module to open its window. The action is module specific, e.g. `mmodAppWall` shows/hides itself when receiving `mmOpen`/`mmClose` messages
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.view] - view to become active
  * @param {string} [params.id] - record to be displayed
  * @param {boolean} [params.replace] - indicates that current breadcrumb item shall be replaced
  */
  mmOpen(sender, params) {
    this.elList.selected = params.id
    this.refreshList()
  } // mmOpen
} // mmodUnits
